import {
    ListItemText,
    MenuItem,
    type MenuItemProps,
    Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import MenuCheck from './MenuCheck';
import MenuRowLead from './MenuRowLead';

/**
 * A choice of a menu's list: a leading status mark and dataset chip when
 * given, the name, a muted meta value, and a teal check on the current one.
 * A disabled reason disables the choice and says why under its name.
 *
 * The other MenuItem props pass through: a Menu steers focus through its
 * direct children's props, so a choice placed directly in a Menu stays
 * reachable from the keyboard.
 */
export default function MenuChoice({
    mark,
    chip,
    name,
    meta,
    selected,
    onClick,
    disabledReason,
    ...menuItemProps
}: Omit<
    MenuItemProps,
    'onClick' | 'disabled' | 'selected' | 'children' | 'sx'
> & {
    /** A status glyph before the chip; `null` keeps its column empty. */
    mark?: ReactNode;
    /** The dataset chip, such as the modality of an image. */
    chip?: ReactNode;
    name: ReactNode;
    meta?: ReactNode;
    selected: boolean;
    onClick: () => void;
    /** Why the choice cannot be made now. */
    disabledReason?: string;
}) {
    return (
        <MenuItem
            {...menuItemProps}
            selected={selected}
            onClick={onClick}
            disabled={disabledReason !== undefined}
        >
            {(mark !== undefined || chip !== undefined) && (
                <MenuRowLead mark={mark} chip={chip} />
            )}
            <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                secondary={disabledReason}
                secondaryTypographyProps={{ whiteSpace: 'normal' }}
            >
                {name}
            </ListItemText>
            {meta && (
                <Typography color="text.secondary" sx={{ flexShrink: 0 }}>
                    {meta}
                </Typography>
            )}
            <MenuCheck selected={selected} />
        </MenuItem>
    );
}
